import type { CSSProperties, ReactNode } from 'react';

export type PillTone = 'good' | 'warn' | 'power' | 'info' | 'accent' | 'mute' | 'danger';

export interface StatusPillProps {
  children: ReactNode;
  tone?: PillTone;
  size?: 'sm' | 'md';
  variant?: 'outline' | 'soft' | 'solid';
  className?: string;
}

/**
 * Small bordered / tinted / filled pill. One of the most-used primitives in
 * the PWA — appears on hero photos, menu rows, list metadata, status
 * indicators. Border, background, and text colour all derive from the
 * semantic tone token, so skin-switching keeps the intent intact.
 */
const TONE_VAR: Record<PillTone, string> = {
  good: 'var(--evi-good)',
  warn: 'var(--evi-warn)',
  power: 'var(--evi-power)',
  info: 'var(--evi-info)',
  accent: 'var(--evi-accent)',
  mute: 'var(--evi-text-mute)',
  danger: 'var(--evi-warn)',
};

export function StatusPill({
  children,
  tone = 'good',
  size = 'sm',
  variant = 'outline',
  className,
}: StatusPillProps) {
  const color = TONE_VAR[tone];
  const padding = size === 'md' ? '5px 10px' : '3px 8px';
  const fontSize = size === 'md' ? '11px' : '10px';

  const variantStyles: Record<NonNullable<StatusPillProps['variant']>, CSSProperties> = {
    outline: {
      background: 'transparent',
      border: `1px solid ${color}`,
      color,
    },
    soft: {
      background: `color-mix(in srgb, ${color} 12%, transparent)`,
      border: 'none',
      color,
    },
    solid: {
      background: color,
      border: 'none',
      // Theme-aware on-accent foreground — the solid variant most
      // often paints over a brand accent (`color` prop = the
      // resolved accent value), so the foreground should follow the
      // per-theme `onAccent` token. Fallback #FFFFFF preserves the
      // long-standing behaviour for SSR / pre-paint and for non-
      // greyscale themes (all of which set onAccent: '#FFFFFF').
      // Note: solid variant also paints `info`/`warn`/`good`/`busy`
      // colours, on which white may not match WCAG perfectly — the
      // semantic-status variants are out of scope for the on-accent
      // drift cleanup; future ticket can introduce per-status
      // foreground tokens if needed.
      color: 'var(--evi-on-accent, #FFFFFF)',
    },
  };

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding,
        borderRadius: 999,
        fontSize,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        fontVariantNumeric: 'tabular-nums',
        ...variantStyles[variant],
      }}
    >
      {children}
    </span>
  );
}
